import React, { useEffect, useRef, useState } from 'react';

import { LoaderSpinner } from '@vw-marketing/us-components';

import { Header } from './components/Header';
import { PersonalInformationForm } from './components/PersonalInformationForm';
import { TermsOfService } from './components/TermsOfService';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { PinForm } from './components/PinForm';
import { MarketingConsent } from './components/MarketingConsent';
import { StyledRegistrationWrapper } from './styles';
import { Confirmation } from './components/Confirmation';
import { useUser } from '../../app/providers/UserContext';
import { useFeatureServices } from '../../shared/hooks/use-feature-services';

enum RegistrationStepCa {
  PERSONAL_INFO = 1,
  TERMS_OF_SERVICE = 2,
  PRIVACY_POLICY = 3,
  MARKETING_CONSENT = 4,
  PIN_SETUP = 5,
  SUCCESS = 6,
}

enum RegistrationStep {
  PERSONAL_INFO = 1,
  PIN_SETUP = 2,
  SUCCESS = 3,
}

type RegistrationStepType = RegistrationStep | RegistrationStepCa;

const Registration: React.FC = () => {
  const { registrationSufficiencyCheck, userRegistrationSufficiencyCheck } = useUser();
  const { 'locale-service': localeService } = useFeatureServices();

  const isCaFlow = localeService?.countryCode === 'CA';

  const pendingProfileCreation =
    registrationSufficiencyCheck?.includes('profileCreation');

  const pendingTermsOfService =
    registrationSufficiencyCheck?.includes('termsOfServiceCreation');

  const pendingPrivacyPolicy =
    registrationSufficiencyCheck?.includes('privacyPolicyCreation');

  const pendingMarketingConsent =
    registrationSufficiencyCheck?.includes('marketingConsentCreation');

  const pendingPinCreation =
    registrationSufficiencyCheck?.includes('securityPinCreation');

  const [currentStep, setCurrentStep] =
    useState<RegistrationStepType | null>(null);

  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    if (isCaFlow) {
      if (pendingProfileCreation) {
        setCurrentStep(RegistrationStepCa.PERSONAL_INFO);
      } else if (pendingTermsOfService) {
        setCurrentStep(RegistrationStepCa.TERMS_OF_SERVICE);
      } else if (pendingPrivacyPolicy) {
        setCurrentStep(RegistrationStepCa.PRIVACY_POLICY);
      } else if (pendingMarketingConsent) {
        setCurrentStep(RegistrationStepCa.MARKETING_CONSENT);
      } else if (pendingPinCreation) {
        setCurrentStep(RegistrationStepCa.PIN_SETUP);
      } else {
        setCurrentStep(RegistrationStepCa.SUCCESS);
      }
    } else {
      if (pendingProfileCreation) {
        setCurrentStep(RegistrationStep.PERSONAL_INFO);
      } else if (pendingPinCreation) {
        setCurrentStep(RegistrationStep.PIN_SETUP);
      } else {
        setCurrentStep(RegistrationStep.SUCCESS);
      }
    }
  }, [
    isCaFlow,
    pendingProfileCreation,
    pendingTermsOfService,
    pendingPrivacyPolicy,
    pendingPinCreation,
    pendingMarketingConsent,
  ]);


  const goToNextStep = async () => {
    await userRegistrationSufficiencyCheck();
    setCurrentStep((prev) =>
      prev !== null ? ((prev + 1) as RegistrationStepType) : null
    );
  };


  const renderCurrentStep = () => {
    if (currentStep === null) {
      return <LoaderSpinner />;
    }

    if (isCaFlow) {
      switch (currentStep as RegistrationStepCa) {
        case RegistrationStepCa.PERSONAL_INFO:
          return (
            <PersonalInformationForm
              key={currentStep}
              onComplete={goToNextStep}
              stepNumber={currentStep as number}
              isCaFlow
            />
          );
        case RegistrationStepCa.TERMS_OF_SERVICE:
          return (
            <TermsOfService
              key={currentStep}
              onComplete={goToNextStep}
            />
          );
        case RegistrationStepCa.PRIVACY_POLICY:
          return (
            <PrivacyPolicy
              key={currentStep}
              onComplete={goToNextStep}
            />
          );
        case RegistrationStepCa.PIN_SETUP:
          return (
            <PinForm
              key={currentStep}
              onComplete={goToNextStep}
            />
          );
        case RegistrationStepCa.MARKETING_CONSENT:
          return (
            <MarketingConsent
              key={currentStep}
              onComplete={goToNextStep}
            />
          );

        case RegistrationStepCa.SUCCESS:
          return <Confirmation key={currentStep} />;

        default:
          return <LoaderSpinner />;
      }
    }

    switch (currentStep as RegistrationStep) {
      case RegistrationStep.PERSONAL_INFO:
        return (
          <PersonalInformationForm
            key={currentStep}
            onComplete={goToNextStep}
          />
        );

      case RegistrationStep.PIN_SETUP:
        return (
          <PinForm
            key={currentStep}
            onComplete={goToNextStep}
          />
        );

      case RegistrationStep.SUCCESS:
        return <Confirmation key={currentStep} />;

      default:
        return <LoaderSpinner />;
    }
  };

const showHeader =
  currentStep !== null &&
  ((isCaFlow && currentStep < RegistrationStepCa.SUCCESS) ||
  (!isCaFlow && currentStep < RegistrationStep.SUCCESS));

  return (
    <StyledRegistrationWrapper className="registration-wrapper">
      {showHeader && <Header currentStep={currentStep} />}
      {renderCurrentStep()}
    </StyledRegistrationWrapper>
  );
};

export default Registration;
